import React, { useEffect } from "react";
import { Box, Button, Grid, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { BaseTextField } from "components/FormBuilder/fields/BaseTextField";
import { BaseDrawer } from "components/Shared/Base/BaseDrawer";
import LoadingWrapper from "components/Shared/LoadingWrapper";
import InnerShadowBox from "components/Shared/InnerShadowBox";
import { BASE_CONSTANTS } from "constants/baseConstants";

const INITIAL_STATE = {
  creditLimit: "",
  codLimit: "",
};

const VendorProfileLimitForm = ({
  isBusy = false,
  vendorLimitDetail = null,
  onSubmit = () => {},
  onCancel = () => {},
  vendorId,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: INITIAL_STATE,
    mode: "onBlur",
  });

  const isEditMode = !!vendorLimitDetail;

  useEffect(() => {
    if (vendorLimitDetail) {
      reset({
        creditLimit: vendorLimitDetail.creditLimit || "",
        codLimit: vendorLimitDetail.codLimit || "",
      });
    } else {
      reset({ ...INITIAL_STATE });
    }
  }, [vendorLimitDetail, reset]);

  const handleLimitSubmit = (data) => {
    const payload = {
      vendorId,
      creditLimit: Number(data.creditLimit),
      codLimit: Number(data.codLimit),
      // When creating, isActive will be set to true by backend
      // When editing, preserve the current isActive state
      ...(isEditMode && { isActive: vendorLimitDetail.isActive }),
    };
    onSubmit(payload);
  };

  return (
    <>
      <BaseDrawer.Header>
        {isEditMode ? "Edit Limit" : "Add Limit"}
      </BaseDrawer.Header>

      <LoadingWrapper loading={isBusy}>
        <InnerShadowBox>
          <BaseDrawer.Content>
            <Grid container spacing={BASE_CONSTANTS.SPACING_LG}>
              <Grid item xs={12}>
                <Controller
                  name="creditLimit"
                  control={control}
                  rules={{
                    required: "Credit Limit is required",
                    min: { value: 0, message: "Credit Limit must be ≥ 0" },
                  }}
                  render={({ field }) => (
                    <BaseTextField
                      fullWidth
                      required
                      label="Credit Limit"
                      type="number"
                      size="small"
                      error={!!errors.creditLimit}
                      helperText={errors.creditLimit?.message}
                      {...field}
                      inputProps={{
                        min: 0,
                        step: "0.01",
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="codLimit"
                  control={control}
                  rules={{
                    min: { value: 0, message: "COD Limit must be ≥ 0" },
                  }}
                  render={({ field }) => (
                    <BaseTextField
                      fullWidth
                      label="COD Limit"
                      type="number"
                      size="small"
                      error={!!errors.codLimit}
                      helperText={errors.codLimit?.message}
                      {...field}
                      inputProps={{
                        min: 0,
                        step: "0.01",
                      }}
                    />
                  )}
                />
              </Grid>

              {isEditMode && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "background.default",
                      borderRadius: 1,
                      border: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      <strong>Note:</strong> Use the toggle switch in the table
                      to activate or deactivate this limit.
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </BaseDrawer.Content>
        </InnerShadowBox>

        <BaseDrawer.Actions>
          <Button
            disabled={isBusy}
            onClick={() => {
              onCancel();
              reset(INITIAL_STATE);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit(handleLimitSubmit)}
            disabled={isBusy}
          >
            {isEditMode ? "Update" : "Add"}
          </Button>
        </BaseDrawer.Actions>
      </LoadingWrapper>
    </>
  );
};

export default VendorProfileLimitForm;
